<script setup>
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'

import dagre from '@dagrejs/dagre'
import { getI18n } from '@/plugins/i18n'
import { ref, nextTick, onMounted, onUnmounted } from 'vue'

import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxPopup } from 'devextreme-vue/popup'
import { DxSplitter, DxItem } from 'devextreme-vue/splitter'

import { usePortalStore } from '@/stores/portal.store'

import { VueFlow, useVueFlow, Position } from '@vue-flow/core'

import * as dataGrid from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'

import DocumentRelationNode from './DocumentRelationNode.vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  fetchDocumentRelations: {
    type: Function,
    default: null,
  }
})

const emit = defineEmits(['update:visible'])

// Use a local ref initialized to false to avoid DxPopup firing 'hiding'
// spuriously during initialization (which would immediately close the dialog).
// We sync it to true after mount via nextTick.
const popupVisible = ref(false)

const onEscKey = (e) => {
  if (e.key === 'Escape' && popupVisible.value) {
    popupVisible.value = false
  }
}

onMounted(() => {
  popupVisible.value = props.visible
  document.addEventListener('keydown', onEscKey)
})

const i18n = getI18n()

const portalStore = usePortalStore()

const { findNode, fitView, updateNodeInternals } = useVueFlow()

const flowVisible = ref(false)
const flowReady = ref(false)
const nodes = ref([])
const edges = ref([])
const selectedNodeId = ref(null)
const gridRef = ref(null)
const localizedDocumentTypes = portalStore.getLocalizedDocumentTypes()

const applyDagreLayout = () => {
  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))
  dagreGraph.setGraph({ rankdir: 'TB', ranksep: 60, nodesep: 40 })

  for (const node of nodes.value) {
    const graphNode = findNode(node.id)
    dagreGraph.setNode(node.id, {
      width: graphNode.dimensions.width || 150,
      height: graphNode.dimensions.height || 50,
    })
  }

  for (const edge of edges.value) {
    dagreGraph.setEdge(edge.source, edge.target)
  }

  dagre.layout(dagreGraph)

  nodes.value = nodes.value.map(node => {
    const nodeWithPosition = dagreGraph.node(node.id)
    return {
      ...node,
      targetPosition: Position.Top,
      sourcePosition: Position.Bottom,
      position: { x: nodeWithPosition.x, y: nodeWithPosition.y },
    }
  })

  nextTick(async() => {
    updateNodeInternals(nodes.value.map(n => n.id))
    await fitView({ padding: 0.3 })
    flowReady.value = true
  })
}

const onPaneReady = () => {
  applyDagreLayout()
  if (selectedNodeId.value) {
    const grid = gridRef.value?.instance
    if (grid) {
      grid.option('focusedRowKey', selectedNodeId.value)
      grid.navigateToRow(selectedNodeId.value)
    }
  }
}

const updateEdgeAnimations = (nodeId) => {
  edges.value = edges.value.map(edge => {
    const active = nodeId != null && (edge.source === nodeId || edge.target === nodeId)
    return {
      ...edge,
      animated: active,
      style: active ? { stroke: '#29b6f6', strokeWidth: 2 } : {},
    }
  })
}

const onNodeClick = ({ node }) => {
  selectedNodeId.value = node.id
  updateEdgeAnimations(node.id)
  const grid = gridRef.value?.instance
  if (grid) {
    grid.option('focusedRowKey', node.id)
    grid.navigateToRow(node.id)
  }
}

const onSplitterResize = async() => {
  await fitView({ padding: 0.3 })
}

const onFocusedRowChanged = (e) => {
  if (e.row?.key != null) {
    const id = String(e.row.key)
    selectedNodeId.value = id
    updateEdgeAnimations(id)
  }
}

const columns = [
  dataGridColumn.useDateTimeColumn({ sortOrder: 'asc' }),
  dataGridColumn.useDateColumn(),
  dataGridColumn.useSeriesColumn({ sortOrder: null }),
  dataGridColumn.useNumberMnemonicColumn({ sortOrder: null }),
  dataGridColumn.useDocumentTypeColumn(),
  dataGridColumn.useCustomerColumn({dataField: 'customer'}),
  dataGridColumn.useTotalNoVatColumn(),
  dataGridColumn.useTotalVatColumn(),
]

const gridOptions = dataGrid.useDefaultGridOptions({
  id: 'document-relation-grid',
  columns,
  keyExpr: 'id',
  remoteOperations: false,
  stateStoring: { enabled: false },
  focusedRowEnabled: true,
  autoNavigateToFocusedRow: true,
  onFocusedRowChanged,
  paging: { enabled: false },
  pager: { visible: false },
  editing: { allowAdding: false, allowUpdating: false, allowDeleting: false },
  scrolling: { mode: 'standard' },
  filterRow: { visible: false },
  hideRefreshButton: true,
  hideClearFilterButton: true,
})

const applyData = async(treeData) => {
  nodes.value = treeData.nodes.map(n => ({
    id: String(n.id),
    type: 'doc-node',
    position: { x: 0, y: 0 },
    data: n,
  }))

  edges.value = treeData.edges.map(e => ({
    ...e,
    source: String(e.source),
    target: String(e.target),
    animated: false,
  }))

  gridRef.value?.instance?.option('dataSource', treeData.nodes)

  const sourceNode = treeData.nodes.find(n => n.is_source)
  if (sourceNode) {
    onNodeClick({ node: { id: String(sourceNode.id) } })
  }

  flowVisible.value = true
  flowReady.value = false
}

onUnmounted(() => document.removeEventListener('keydown', onEscKey))

const onHiding = () => {
  popupVisible.value = false
  flowVisible.value = false
  emit('update:visible', false)
}

const onShown = async() => {
  if (props.fetchDocumentRelations) {
    const { data } = await props.fetchDocumentRelations()
    data.nodes = data.nodes.map(n => {
      n.name = localizedDocumentTypes.find(x => x.id === n.document_type)?.name
      n.color = localizedDocumentTypes.find(x => x.id === n.document_type)?.color
      return n
    })
    await applyData(data)
  }
}
</script>

<template>
  <dx-popup
    v-model:visible="popupVisible"
    height="80vh"
    width="90vw"
    :title="i18n.t('common.documentRelation', 2)"
    :hide-on-outside-click="true"
    @shown="onShown"
    @hiding="onHiding"
  >
    <div class="relation-wrapper">
      <DxSplitter
        orientation="horizontal"
        class="relation-content"
        @resize-end="onSplitterResize"
      >
        <!-- Left panel: Vue Flow graph -->
        <DxItem
          :size="'30%'"
          min-size="20%"
        >
          <VueFlow
            v-if="flowVisible"
            v-model:nodes="nodes"
            v-model:edges="edges"
            class="relation-flow"
            :style="{ visibility: flowReady ? 'visible' : 'hidden' }"
            @pane-ready="onPaneReady"
            @node-click="onNodeClick"
            @pane-click="selectedNodeId = null; updateEdgeAnimations(null)"
          >
            <template #node-doc-node="{ data, id, targetPosition, sourcePosition }">
              <document-relation-node
                :id="id"
                :data="data"
                :target-position="targetPosition || Position.Top"
                :source-position="sourcePosition || Position.Bottom"
                :selected="selectedNodeId === id"
                :type-label="data.name"
                :type-color="data.color"
              />
            </template>
          </VueFlow>
        </DxItem>

        <!-- Right panel: document grid -->
        <DxItem
          :size="'70%'"
          min-size="20%"
        >
          <DxDataGrid
            ref="gridRef"
            v-bind="gridOptions"
          >
            <template #documentTypeCellTemplate="{ data: templateOptions }">
              <q-chip
                size="sm"
                class="q-ma-none"
                text-color="white"
                style="margin: 0"
                :label="portalStore.getLocalizedDocumentTypes().find(x => x.id === templateOptions.data.document_type)?.name"
                :style="{ backgroundColor: portalStore.getLocalizedDocumentTypes().find(x => x.id === templateOptions.data.document_type)?.color }"
              />
            </template>
          </DxDataGrid>
        </DxItem>
      </DxSplitter>
      <div class="relation-footer">
        <q-btn
          flat
          color="primary"
          :label="i18n.t('common.close')"
          @click="popupVisible = false"
        />
      </div>
    </div>
  </dx-popup>
</template>

<style scoped lang="scss">
.relation-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.relation-content {
  flex: 1;
  min-height: 0;
}

.relation-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 0.5rem;
  flex-shrink: 0;
}

.relation-flow {
  width: 100%;
  height: 100%;
}

</style>
